import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ICoupon } from '../../../models/coupons.model'
import { doGetCoupons } from '../../../store/coupon.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';
import qs from 'qs';
import _ from 'lodash';

import { useSnackbar } from 'notistack';
import {DatePicker, Table, TableColumnsType } from 'antd';


import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Pagination from '../../../components/Pagination';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import config from '../../../lib/config';
import { errorVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';

const BusinessCoupon: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { coupons, isLoading } = useSelector((state: RootState) => state.coupon);

  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [couponData, setCouponData] = useState<ICoupon[]>([]);
  const [page, setPage] = useState(1);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [keyword, setKeyword] = useState('');
  const [param, setParam] = useState({});

  useEffect(() => {
    getCoupons();
  }, [])

  useEffect(() => {
    if (coupons) {
      // query to state
      const _param: any = {};

      if (query.page) {
        setPage(Number(query.page));
        _param.page = Number(query.page);
      }
      if (query.startDate) {
        setStartDate(String(query.startDate));
        _param.startDate = String(query.startDate);
      }
      if (query.endDate) {
        setEndDate(String(query.endDate));
        _param.endDate = String(query.endDate);
      }
      if (query.keyword) {
        setKeyword(String(query.keyword));
        _param.keyword = String(query.keyword);
      }

      setParam(_param);

      // data filtering
      let data: any = _.cloneDeep(coupons);

      data = data.filter((item: any) => item.couponType === 'OT');

      if (query.startDate && query.endDate) {
        data = data.filter((item: any) => moment(item.createSrtDate).add(1, 'days').isAfter(String(query.startDate)));
        data = data.filter((item: any) => moment(item.createEndDate).subtract(1, 'days').isBefore(String(query.endDate)));
      }
      if (query.keyword) {
        data = data.filter((item: any) => item.couponName.indexOf(String(query.keyword)) !== -1);
      }

      setCouponData(data);
    }
  }, [location, coupons]);

  const getCoupons = async () => {
    const res = await dispatch(doGetCoupons());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      case 404:
        enqueueSnackbar(t('common.message.notFound'), warningVariant);
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onSearch = () => {
    const param: any = { page: 1 };

    if (startDate) param.startDate = startDate;
    if (endDate) param.endDate = endDate;
    if (keyword) param.keyword = keyword;

    setParam(param);
    history.push(`/business/coupon?${qs.stringify(param)}`);
  }

  const columns: TableColumnsType<any> = [
    {
      title: 'No.',
      dataIndex: 'couponCode',
      key: 'couponCode',
      width: '6%',
      sorter: (a, b) => a.couponCode - b.couponCode
    },
    {
      title: t('business.coupon.column1'),
      dataIndex: 'couponName',
      key: 'couponName',
      align: 'center',
      render: value => <div className="text-left ant-table-cell-ellipsis">{value}</div>,
      sorter: (a, b) => a.couponName?.localeCompare(b.couponName)
    },
    {
      title: t('business.coupon.column2'),
      dataIndex: 'createSrtDate',
      key: 'createSrtDate',
      width: '15%',
      sorter: (a, b) => a.createSrtDate?.localeCompare(b.createSrtDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD')
    },
    {
      title: t('business.coupon.column3'),
      dataIndex: 'createEndDate',
      key: 'createEndDate',
      width: '15%',
      sorter: (a, b) => a.createEndDate?.localeCompare(b.createEndDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD')
    },
    {
      title: t('business.coupon.column4'),
      dataIndex: 'validDays',
      key: 'validDays',
      width: '15%',
      sorter: (a, b) => a.validDays - b.validDays,
    },
    {
      title: t('business.coupon.column5'),
      dataIndex: 'amount',
      key: 'amount',
      width: '15%',
      sorter: (a, b) => a.amount - b.amount,
      render: (value: string) => value.toLocaleString()
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-business-big.svg"
      parentMenu={t('common.menu.business')}
      currentMenu={t('business.coupon.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('business.coupon.column6')}</label>
            <div className="date-picker__area">
              <DatePicker
                value={startDate ? moment(startDate) : undefined}
                onChange={e => {
                  if (e === null) setStartDate('');
                  else setStartDate(moment(e).format('YYYY-MM-DD'))
                }}
              />
              ~
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={e => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYY-MM-DD'))
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('business.coupon.placeholder1')}</label>
            <div className="input-form-item">
              <input
                type="text"
                className="form-control"
                placeholder={t('business.coupon.placeholder1')}
                value={keyword}
                onChange={(e => setKeyword(e.target.value))}
                onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className='btn-basic small black'
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>



    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      
      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{couponData?.length}</b>{t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            <Button
              className='btn-basic pink'
              onClick={() => history.push(`/business/coupon/create?${qs.stringify(param)}`)}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={couponData || undefined}
            pagination={{
              current: page,
              pageSize: config.pageSize
            }}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(`/business/coupon/${record.couponCode}/edit?${qs.stringify(param)}`);
                }
              };
            }}
            rowClassName="pointer"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={couponData?.length || 0}
              page={page}
              param={param}
              path="/business/coupon"
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className='btn-basic pink'
              onClick={() => history.push(`/business/coupon/create?${qs.stringify(param)}`)}
              text={t('common.button.create')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
    </>
  )
}

export default BusinessCoupon;
